import { QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { AllTasksPageQueryDocument, ProjectTaskFilterInput, ProjectTaskSortInput, ProjectTaskStatus, SortEnumType } from 'gql/index';
import { fetchData } from '../../../../gql/graphql-fetcher';
import { useGetMe } from '../../../../utils/useGetMe';
import { useTaskMessages } from '@modules/tasks/taskUtils';
import { AllTasksPageQueryQuery, AllTasksPageQueryQueryVariables, ProjectTaskFragmentFragment } from 'gql/index';
import { graphql } from 'gql/gql';
export type OwnershipFilterKey = 'mine' | 'all';
export type TimelineFilterKey = 'today' | 'upcoming' | 'completed';

type TaskAssigneeGroup = ProjectTaskFragmentFragment['assigneeGroups'][number] & { members?: { id: string }[] };

export type Task = Omit<ProjectTaskFragmentFragment, 'assigneeGroups'> & {
  completedOn?: string | null;
  assigneeGroups: TaskAssigneeGroup[];
};

const PAGE_SIZE = 50;

export const allTasksPageQueryKeys = {
  root: ['all-tasks-page-query'] satisfies QueryKey,
  list: (where: ProjectTaskFilterInput, order: ProjectTaskSortInput[]) =>
    ['all-tasks-page-query', { where, order }] satisfies QueryKey,
};

graphql(`
  query allTasksPageQuery($first: Int!, $after: String, $where: ProjectTaskFilterInput, $order: [ProjectTaskSortInput!]) {
    tasks(first: $first, after: $after, where: $where, order: $order) {
      edges {
        cursor
        node {
          ...ProjectTaskFragment
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`);

type TasksFiltersContextValue = {
  search: string;
  setSearch: (value: string) => void;
  ownershipFilter: OwnershipFilterKey;
  setOwnershipFilter: (value: OwnershipFilterKey) => void;
  timelineFilter: TimelineFilterKey;
  setTimelineFilter: (value: TimelineFilterKey) => void;
  tasks: ProjectTaskFragmentFragment[];
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage?: boolean;
  fetchNextPage: () => void;
};

const TasksFiltersContext = createContext<TasksFiltersContextValue | undefined>(undefined);

export const TasksFiltersProvider: React.FC<{ children: React.ReactNode; }> = ({ children }) => {
  const { me } = useGetMe();
  const currentUserId = me?.id;

  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilterKey>('mine');
  const [timelineFilter, setTimelineFilter] = useState<TimelineFilterKey>('today');
  const [search, setSearch] = useState('');
  const { getTaskName } = useTaskMessages();
  const ownershipWhere = useMemo<ProjectTaskFilterInput | undefined>(() => {
    if (ownershipFilter !== 'mine' || !currentUserId) {
      return undefined;
    }

    return {
      or: [
        { assignees: { some: { id: { eq: currentUserId } } } },
        { assigneeGroups: { some: { members: { some: { id: { eq: currentUserId } } } } } }
      ]
    };
  }, [ownershipFilter, currentUserId]);

  const where = useMemo<ProjectTaskFilterInput>(() => {
    const filter: ProjectTaskFilterInput = {};

    if (ownershipWhere?.or) {
      filter.or = ownershipWhere.or;
    }

    if (timelineFilter === 'completed') {
      filter.status = { eq: ProjectTaskStatus.Done };
    } else {
      filter.status = { in: [ProjectTaskStatus.Ready, ProjectTaskStatus.InProgress] };
    }

    return filter;
  }, [ownershipWhere, timelineFilter]);

  const orderBy = useMemo<ProjectTaskSortInput[]>(() => {
    if (timelineFilter === 'completed') {
      return [{ completedOn: SortEnumType.Desc }];
    }

    if (timelineFilter === 'upcoming') {
      return [{ startDate: SortEnumType.Asc }, { name: SortEnumType.Asc }];
    }

    return [{ dueDate: SortEnumType.Asc }, { name: SortEnumType.Asc }];
  }, [timelineFilter]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading
  } = useInfiniteQuery({
    queryKey: allTasksPageQueryKeys.list(where, orderBy),
    initialPageParam: undefined,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      fetchData<AllTasksPageQueryQuery, AllTasksPageQueryQueryVariables>(
        AllTasksPageQueryDocument,
        {
          first: PAGE_SIZE,
          after: pageParam,
          where,
          order: orderBy
        }
      )(),
    getNextPageParam: lastPage =>
      lastPage.tasks?.pageInfo.hasNextPage
        ? lastPage.tasks?.pageInfo.endCursor ?? undefined
        : undefined,
  });

  const tasks = useMemo(() => (data?.pages ?? []).flatMap(page => page.tasks?.edges?.map(edge => edge.node) ?? []), [data]);

  const filteredTasks = useMemo(() => {
    const now = dayjs();
    const endOfToday = dayjs().endOf('day');
    const term = search.toLowerCase();


    const applyTimeline = (task: ProjectTaskFragmentFragment) => {
      if (timelineFilter === 'today') {
        return (task.dueDate != null && !dayjs(task.dueDate).isAfter(endOfToday) 
        || (task.dueDate == null && task.startDate != null && !dayjs(task.startDate).isAfter(now)));
      }

      if (timelineFilter === 'upcoming') {
        return (task.startDate != null && dayjs(task.startDate).isAfter(endOfToday)
        || (task.dueDate != null && dayjs(task.dueDate).isAfter(endOfToday)));
      }

      if (timelineFilter === 'completed') {
        return task.status === ProjectTaskStatus.Done;
      }

      return true;
    };

    const applySearch = (task: ProjectTaskFragmentFragment) =>
      term === ''
      || getTaskName(task).toLowerCase().includes(term)
      || (task.project?.name ?? '').toLowerCase().includes(term)
      || (task.document?.fileName ?? '').toLowerCase().includes(term)
      || (task.projectForm?.formDefinition?.name ?? '').toLowerCase().includes(term);

    const sortByDueDate = (left: ProjectTaskFragmentFragment, right: ProjectTaskFragmentFragment) => {
      const leftDate = left.dueDate ?? left.startDate;
      const rightDate = right.dueDate ?? right.startDate;

      // Place tasks without any date at the end.
      if (!leftDate && !rightDate) return 0;
      if (!leftDate) return 1;
      if (!rightDate) return -1;

      const leftValue = dayjs(leftDate).valueOf();
      const rightValue = dayjs(rightDate).valueOf();

      if (timelineFilter === 'completed') {
        return rightValue - leftValue; // most recently due first
      }

      return leftValue - rightValue; // oldest first
    };

    const sortByCompletedOnDesc = (left: ProjectTaskFragmentFragment, right: ProjectTaskFragmentFragment) => {
      const leftCompletedOn = left.completedOn ? dayjs(left.completedOn).valueOf() : 0;
      const rightCompletedOn = right.completedOn ? dayjs(right.completedOn).valueOf() : 0;
      return rightCompletedOn - leftCompletedOn;
    };

    let filteredTasks = [...tasks]
      .filter(task => applyTimeline(task))
      .filter(task => applySearch(task));
    
    if (timelineFilter === 'completed') {
      filteredTasks = filteredTasks.sort(sortByCompletedOnDesc);
    } else {
      filteredTasks = filteredTasks.sort(sortByDueDate);
    }

    return filteredTasks;
  }, [currentUserId, ownershipFilter, search, tasks, timelineFilter]);

  const value: TasksFiltersContextValue = useMemo(() => ({
    search,
    setSearch,
    ownershipFilter,
    setOwnershipFilter,
    timelineFilter,
    setTimelineFilter,
    tasks: filteredTasks,
    isLoading: isLoading,
    isFetching: isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  }), [
    fetchNextPage,
    filteredTasks,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    ownershipFilter,
    search,
    timelineFilter
  ]);

  return (
    <TasksFiltersContext.Provider value={value}>
      {children}
    </TasksFiltersContext.Provider>
  );
};

export const useTasksFilters = (): TasksFiltersContextValue => {
  const context = useContext(TasksFiltersContext);
  if (!context) {
    throw new Error('useTasksFilters must be used within TasksFiltersProvider');
  }

  return context;
};

